/**
 * WYSIWYG Product Embed Render
 * Builds the toolbar button, plus the Quill embed value inserted when a
 * product is picked from it (see wysiwyg-product-embed-blot-utils.ts).
 *
 * The card markup itself is NOT a separate design — it reuses
 * ProductLinkerRender.buildCardHtml(), the same "linked product" card used by
 * the single-product "Target Product" preview (post-product-linker), so an
 * embedded product in an article looks identical to that common component
 * rather than inventing its own simplified look.
 */

import { translator } from "../../translation/translator";
import { ProductLinkerRender } from "../../post/linker/product-linker-render";
import type { ProductLinkerProduct } from "../../post/linker/product-linker-api";
import type { ProductEmbedBlotValue } from "./wysiwyg-product-embed-blot-utils";

class WysiwygProductEmbedRender {
    public static createToolbarButton(toolbarContainer: HTMLElement): HTMLButtonElement {
        const group = document.createElement("span");
        group.className = "ql-formats";

        const button = document.createElement("button");
        button.type = "button";
        button.className = "ql-product-embed";
        button.setAttribute("tabindex", "-1");
        button.title = translator?.getTranslation("wysiwyg_insert_product") || "Insert product";

        group.appendChild(button);
        toolbarContainer.appendChild(group);

        return button;
    }

    /**
     * Builds the Quill embed value (see wysiwyg-product-embed-blot-utils.ts)
     * for a picked product: the product ID (kept as its own attribute so a
     * saved post's content can be re-parsed later) plus the common
     * "post-product-linker__card" markup as the inner content.
     */
    public static buildEmbedValue(product: ProductLinkerProduct): ProductEmbedBlotValue | null {
        const productId = String(product?.product_id ?? "").trim();
        if (!productId) return null;

        const html = ProductLinkerRender.buildCardHtml(product, {
            targetBlank: true,
            metaDirection: "horizontal",
        });

        return { productId, html };
    }
}

export { WysiwygProductEmbedRender };
